import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import { Paid, Assessment, TrendingUp, HourglassEmpty, Download } from '@mui/icons-material';
import CalculatedIcon from '../components/icons/CalculatedIcon';
import { apiService } from '../services/api';
import { format } from 'date-fns';

const PERIOD_OPTIONS = [
  { value: 'MTD', label: 'Month to Date' },
  { value: 'QTD', label: 'Quarter to Date' },
  { value: 'YTD', label: 'Year to Date' },
  { value: 'L30', label: 'Last 30 Days' },
];

const numberFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const FREQUENCY_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  'bi-weekly': 'Bi-Weekly',
  monthly: 'Monthly',
  'per-instance': 'Per Instance',
  'seasonal-contract': 'Seasonal Contract',
};

const SERVICE_TYPE_LABELS: Record<string, string> = {
  mowing: 'Mowing',
  fertilizing: 'Weed Control',
  snow_removal: 'Snow Removal',
  other: 'Other Services',
};

interface FinancialSummary {
  period: string;
  periodLabel: string;
  startDate: string;
  endDate: string;
  totalGrossRevenue: number;
  grossRevenueYtd: number;
  totalPlatformFees: number;
  newCustomerFees: number;
  onboardedCustomerFees: number;
  totalProviderEarnings: number;
  averageTicketSize: number;
  grossMargin: number;
  netIncome: number;
  totalPayoutsProcessed: number;
  totalOutstandingPayouts: number;
  outstandingPayoutsCount: number;
  totalDisputedAmount: number;
  taxWithholdingAmount: number;
}

interface OutstandingPayout {
  payoutId: string;
  providerName: string;
  weekStartDate?: string;
  weekEndDate?: string;
  status: string;
  providerCut: number;
  jobCount: number;
}

interface AggregatedMetrics {
  averageGrossRevenue?: number;
  averagePlatformFeeRate?: number;
  topProvidersByRevenue: Array<{
    providerId: string;
    providerName: string;
    totalRevenue: number;
    totalJobs: number;
    platformFees: number;
  }>;
}

interface ServiceInsights {
  customerMix: {
    uniqueProviders: number;
    newCustomers: number;
    onboardedCustomers: number;
  };
  frequencyDistribution: Array<{ frequency: string; count: number }>;
  serviceTypeDistribution: Array<{ serviceType: string; count: number }>;
}

const payoutStatusColorMap: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'warning',
  ready: 'info',
  processed: 'success',
  paid: 'success',
  failed: 'error',
  processing: 'info',
};

const AdminFinancialsPage: React.FC = () => {
  const [period, setPeriod] = useState<string>('MTD');
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [outstandingPayouts, setOutstandingPayouts] = useState<OutstandingPayout[]>([]);
  const [metrics, setMetrics] = useState<AggregatedMetrics | null>(null);
  const [serviceInsights, setServiceInsights] = useState<ServiceInsights | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchFinancials = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.get(`/admin/financials?period=${period}`);
        if (!response.success) {
          throw new Error(response.error || 'Failed to load financial summary');
        }
        const data = response.data as {
          summary: FinancialSummary;
          outstandingPayouts: OutstandingPayout[];
          aggregatedMetrics: AggregatedMetrics;
          serviceInsights: ServiceInsights;
        };
        setSummary(data.summary);
        setOutstandingPayouts(data.outstandingPayouts || []);
        setMetrics(data.aggregatedMetrics || null);
        setServiceInsights(data.serviceInsights || null);
      } catch (err: any) {
        setError(err.message || 'Failed to load financial summary');
      } finally {
        setLoading(false);
      }
    };

    fetchFinancials();
  }, [period]);

  const summaryCards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        label: 'Gross Revenue',
        value: numberFormatter.format(summary.totalGrossRevenue),
        icon: <TrendingUp color="primary" />,
        subtitle: `${summary.periodLabel}`,
      },
      {
        label: 'Gross Revenue (YTD)',
        value: numberFormatter.format(summary.grossRevenueYtd),
        icon: <TrendingUp color="success" />,
        subtitle: 'Year to Date',
      },
      {
        label: 'Platform Fees',
        value: numberFormatter.format(summary.totalPlatformFees),
        icon: <CalculatedIcon color="info" />,
        subtitle: `${percentFormatter.format((summary.totalPlatformFees || 0) / (summary.totalGrossRevenue || 1))} of revenue`,
      },
      {
        label: 'Provider Earnings',
        value: numberFormatter.format(summary.totalProviderEarnings),
        icon: <Paid color="success" />,
        subtitle: 'Net paid out to providers',
      },
      {
        label: 'Net Income',
        value: numberFormatter.format(summary.netIncome),
        icon: <Assessment color="primary" />,
        subtitle: 'Platform margin after provider payouts',
      },
      {
        label: 'Outstanding Payouts',
        value: numberFormatter.format(summary.totalOutstandingPayouts),
        icon: <HourglassEmpty color="warning" />,
        subtitle: `${summary.outstandingPayoutsCount} payouts pending`,
      },
    ];
  }, [summary]);

  const frequencyData = serviceInsights?.frequencyDistribution ?? [];
  const serviceTypeData = serviceInsights?.serviceTypeDistribution ?? [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Financial Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive revenue, fee, and payout visibility for the platform.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Select size="small" value={period} onChange={(event) => setPeriod(event.target.value)}>
            {PERIOD_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Button variant="outlined" startIcon={<Download />}>
            Export CSV
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.label}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">{card.label}</Typography>
                      {card.icon}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.subtitle}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Customer & service mix */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Customer & Provider Mix
                  </Typography>
                  {serviceInsights ? (
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Providers
                        </Typography>
                        <Typography variant="h6">{serviceInsights.customerMix.uniqueProviders}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          New Customers
                        </Typography>
                        <Typography variant="h6">{serviceInsights.customerMix.newCustomers}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Onboarded Customers
                        </Typography>
                        <Typography variant="h6">{serviceInsights.customerMix.onboardedCustomers}</Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Mix data unavailable for this period.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Frequencies
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Frequency</TableCell>
                          <TableCell align="right">Services</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {frequencyData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No services in this period
                            </TableCell>
                          </TableRow>
                        ) : (
                          frequencyData.map((entry) => (
                            <TableRow key={entry.frequency}>
                              <TableCell>{FREQUENCY_LABELS[entry.frequency] || entry.frequency}</TableCell>
                              <TableCell align="right">{entry.count}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Types
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Services</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serviceTypeData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No services in this period
                            </TableCell>
                          </TableRow>
                        ) : (
                          serviceTypeData.map((entry) => (
                            <TableRow key={entry.serviceType}>
                              <TableCell>{SERVICE_TYPE_LABELS[entry.serviceType] || entry.serviceType}</TableCell>
                              <TableCell align="right">{entry.count}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Outstanding payouts */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Outstanding Payouts</Typography>
                <Chip label={`${summary?.outstandingPayoutsCount || 0} pending`} color="warning" />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payout</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Week</TableCell>
                      <TableCell align="right">Jobs</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outstandingPayouts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No outstanding payouts 🎉
                        </TableCell>
                      </TableRow>
                    ) : (
                      outstandingPayouts.map((payout) => (
                        <TableRow key={payout.payoutId}>
                          <TableCell>
                            <Typography variant="subtitle2">{payout.payoutId.slice(-8).toUpperCase()}</Typography>
                          </TableCell>
                          <TableCell>{payout.providerName}</TableCell>
                          <TableCell>
                            {payout.weekStartDate && payout.weekEndDate
                              ? `${format(new Date(payout.weekStartDate), 'MMM d')} – ${format(
                                  new Date(payout.weekEndDate),
                                  'MMM d',
                                )}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell align="right">{payout.jobCount}</TableCell>
                          <TableCell align="right">{numberFormatter.format(payout.providerCut || 0)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={payout.status}
                              color={payoutStatusColorMap[payout.status] || 'default'}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Aggregated provider metrics */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Providers by Revenue
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Jobs</TableCell>
                      <TableCell align="right">Fees</TableCell>
                      <TableCell align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics?.topProvidersByRevenue?.length ? (
                      metrics.topProvidersByRevenue.map((provider) => (
                        <TableRow key={provider.providerId}>
                          <TableCell>{provider.providerName}</TableCell>
                          <TableCell align="right">{numberFormatter.format(provider.totalRevenue || 0)}</TableCell>
                          <TableCell align="right">{provider.totalJobs}</TableCell>
                          <TableCell align="right">{numberFormatter.format(provider.platformFees || 0)}</TableCell>
                          <TableCell align="right">{numberFormatter.format((provider.totalRevenue || 0) - (provider.platformFees || 0))}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No provider revenue data for this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  );
};

export default AdminFinancialsPage;

